import {
  type ChangeEvent,
  Fragment,
  type MutableRefObject,
  type Ref,
  forwardRef,
  useRef,
  useState,
} from 'react'

import { Box, Hint, SearchField, dimensions } from '@factorialco/deprecated-design-system'
import * as Sentry from '@sentry/browser'
import { LOCAL_STORAGE_KEYS_FACTORIAL_TIPS } from 'config/Vars'
import { capitalize } from 'lodash'

import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { useMemo, useUnsafeMemo } from 'modules/core/hooks/useMemo'
import { useTextSearch } from 'modules/core/hooks/useTextSearch'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import buildUrl from 'modules/core/lib/buildUrl'
import i18n from 'modules/core/lib/i18n'
import useLocalStorage from 'modules/core/lib/useLocalStorage'
import app from 'modules/core/store/models/app'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import ActionBar from 'modules/org_chart/components/ActionBar'
import Frame from 'modules/org_chart/components/Frame'
import Header from 'modules/org_chart/components/Header'
import Node from 'modules/org_chart/components/Node'
import type { NodeStatus } from 'modules/org_chart/components/Node/Employee'
import { getEmployeeJobPosition } from 'modules/org_chart/lib/getEmployeeJobPosition'
import { NODE_SIZE } from 'modules/org_chart/components/Node/constants'
import { useOrgChartState } from 'modules/org_chart/components/Provider'
import SearchResultsNavigator from 'modules/org_chart/components/SearchResultsNavigator'
import SidePanel from 'modules/org_chart/components/SidePanel'
import TagInfoSelect from 'modules/org_chart/components/TagInfoSelect'
import useTree from 'modules/org_chart/hooks/useTree'
import useUncollapsedNodes from 'modules/org_chart/hooks/useUncollapsedNodes'
import useZoom from 'modules/org_chart/hooks/useZoom'
import type { GraphQLEmployee } from 'modules/org_chart/lib/employeeUtils'
import linkGenerator from 'modules/org_chart/lib/linkGenerator'
import { takeSnapshot } from 'modules/org_chart/lib/takeSnapshot'
import { filterVisibleTreeNodes } from 'modules/org_chart/lib/filterVisibleTreeNodes'
import { getEmployeeNodeStatus } from 'modules/org_chart/lib/getEmployeeNodeStatus'
import { findTreeNodeById } from 'modules/org_chart/lib/tree'
import type { Dimensions } from 'modules/org_chart/models/Dimensions'
import type { PointNode } from 'modules/org_chart/models/PointNode'
import type { VisibleBounds } from 'modules/org_chart/models/VisibleBounds'

const getPointNodeKey = (pointNode: PointNode | null | undefined): string => {
  if (!pointNode) {
    return 'root'
  }

  switch (pointNode.data.type) {
    case 'employee':
      return `employee-${pointNode.data.employee.id}`
    case 'position':
      return `position-${pointNode.data.positionId}`
    case 'add-position':
      return `add-position-${pointNode.data.managerId}`
    default:
      return 'root'
  }
}

export interface Props {
  heads: GraphQLEmployee[]
  employees: GraphQLEmployee[]
  size: Dimensions
  ref: Ref<SVGSVGElement>
  showLegalGenders: boolean
  canExport: boolean
  canSnapshot: boolean
  canManagePositions: boolean
  canSeeJobPostings: boolean
  canCreateJobPostings: boolean
  canSeeJobTitle: boolean
  canSeeJobCatalog: boolean
  canSeeJobCatalogTree: boolean
  hasLegacyJobCatalog: boolean
  allEmployees: GraphQLEmployee[]
  employeeMap: Map<string, GraphQLEmployee>
}

const OrgChartRef = forwardRef<SVGSVGElement, Props>(function OrgChart(
  {
    heads,
    employees,
    size,
    showLegalGenders,
    canExport,
    canSnapshot,
    canManagePositions,
    canSeeJobPostings,
    canCreateJobPostings,
    canSeeJobTitle,
    canSeeJobCatalog,
    canSeeJobCatalogTree,
    hasLegacyJobCatalog,
    allEmployees,
    employeeMap,
  },
  ref
) {
  const { update, hintedNode, searchResultId, tagInfoProp } = useOrgChartState()
  const [width, height] = size
  const frameHeight = useMemo(() => height - parseInt(dimensions.s80), [height])
  const svgRef = useRef<SVGSVGElement>(null)
  const contentRef = useRef<SVGGElement>(null)
  const uncollapsedNodes = useUncollapsedNodes(new Set(heads.map((x) => x.id)))
  const tree = useTree(
    heads,
    employees,
    uncollapsedNodes.values,
    width,
    canManagePositions,
    canSeeJobPostings,
    canCreateJobPostings,
    allEmployees,
    employeeMap
  )
  /**
   * Bounds of the region we render. `undefined` means "render everything"
   * (used until the zoom hook has wired up and reported the real viewport).
   * The zoom hook only calls `setVisibleBounds` when the user has panned
   * past the 1-viewport padding, so this state rarely updates during pan.
   */
  const [visibleBounds, setVisibleBounds] = useState<VisibleBounds | undefined>(undefined)
  const zoom = useZoom({
    svgElement: svgRef.current,
    contentElement: contentRef.current,
    size: [width, frameHeight],
    initialCoordinates: tree.initialCoordinates,
    onVisibleBoundsChange: setVisibleBounds,
  })
  const isFullyUncollapsed = employees.length <= uncollapsedNodes.values.size
  const [hideOrgChartPositionsHints, setHideOrgChartPositionsHints] = useLocalStorage<boolean>(
    LOCAL_STORAGE_KEYS_FACTORIAL_TIPS.hideOrgChartPositionsTip
  )
  const [search, setSearch] = useState('')
  const [highlightedEmployeeId, setHighlightedEmployeeId] = useState<string>()
  const [pendingNavigationRequest, setPendingNavigationRequest] = useState<
    { id: string; nonce: number } | undefined
  >()

  const onSearch = (event: ChangeEvent<HTMLInputElement>) => {
    const val = event.target.value
    setHighlightedEmployeeId(undefined)
    setPendingNavigationRequest(undefined)
    setSearch(val)
  }

  const { employee: currentSessionEmployee } = useCurrentSession()
  const currentEmployee = currentSessionEmployee?.id
    ? employeeMap.get(currentSessionEmployee.id)
    : undefined

  const { results } = useTextSearch(employees, search, {
    fields: [],
    storeFields: ['id'],
    extraFields: ['jobTitle', 'fullName'],
    extraStoreFields: [],
    fuzzy: 0.1,
    skipIndexing: employees.length === 0,
    extractField: (employee, field) => {
      if (field === 'jobTitle') {
        return getEmployeeJobPosition(employee.referenceContract, {
          canSeeJobTitle,
          canSeeJobCatalog,
          canSeeJobCatalogTree,
          hasLegacyJobCatalog,
        })
      }

      if (field === 'fullName') {
        return employee.fullName
      }

      if (field === 'id') {
        return String(employee.id)
      }

      return ''
    },
  })

  const filteredEmployees = useUnsafeMemo(() => {
    if (!search) {
      return employees
    }

    return results
      .map((employee) => employeeMap.get(String(employee.id)))
      .filter((employee): employee is GraphQLEmployee => employee !== undefined)
  }, [employeeMap, employees, results, search])

  const filteredEmployeeIds = useUnsafeMemo(() => {
    if (!search) {
      return undefined
    }

    return new Set(results.map((employee) => String(employee.id)))
  }, [results, search])

  const handleFindMe = () => {
    if (!currentEmployee) {
      // TODO;
      // Add modal to infom the user they arent' in the Org Chart
      return
    } else {
      track(trackingEvents.orgChart.findMeButton.click)
      setHighlightedEmployeeId(currentEmployee.id)
      handleResultChange(undefined, currentEmployee, false)
    }
  }

  const handleUncollapseAll = () => {
    if (isFullyUncollapsed) {
      uncollapsedNodes.reset()
      zoom.reset()
    } else {
      uncollapsedNodes.expand(employees.map((x) => x.id))

      const scaleAmount = zoom.scaleRange.MAX_ZOOM - zoom.scaleRange.MIN_ZOOM
      zoom.scale(scaleAmount)
    }
  }

  const handleResultChange = (
    result: GraphQLEmployee | undefined,
    selectedEmployee: GraphQLEmployee | undefined,
    resetExpandedPath = false
  ) => {
    if (!!search || !!selectedEmployee) {
      if (result) {
        setHighlightedEmployeeId(undefined)
      }
      const id = result ? result.id : selectedEmployee?.id
      update('searchResultId', id)

      if (!id) {
        setPendingNavigationRequest(undefined)
        return
      }

      const treeResult = selectedEmployee ?? result

      if (!treeResult) return

      uncollapsedNodes.expand(tree.traceTreePathFromEmployee(treeResult), resetExpandedPath)
      setPendingNavigationRequest({ id, nonce: performance.now() })
    }
  }

  const handleHintChange = () => {
    if (!hintedNode) return

    const manager = employeeMap.get(hintedNode.data.managerId)

    update('activeSidePanelTab', 'reports')
    update('activeEmployee', manager)
    setHideOrgChartPositionsHints(true)
    track(trackingEvents.orgChart.nodes.addPosition.hint.action)
  }

  const handleHintClose = () => {
    update('hintedNode', undefined)
    setHideOrgChartPositionsHints(true)
    track(trackingEvents.orgChart.nodes.addPosition.hint.close)
  }

  const handleHintFinish = () => {
    update('hintedNode', undefined)
    setHideOrgChartPositionsHints(true)
    track(trackingEvents.orgChart.sidepanel.assignJobsHint.action)
  }

  useUnsafeEffect(() => {
    if (!ref) return // eslint-disable-next-line no-type-assertion/no-type-assertion
    ;(ref as MutableRefObject<SVGSVGElement>).current = svgRef.current as SVGSVGElement
  }, [svgRef, ref])

  useUnsafeEffect(() => {
    if (!pendingNavigationRequest || !zoom.isReady) {
      return
    }

    const pointNode = findTreeNodeById(tree.tree, pendingNavigationRequest.id)

    if (!pointNode) {
      setPendingNavigationRequest(undefined)
      return
    }

    zoom.navigateToNode(pointNode)
    setPendingNavigationRequest(undefined)
  }, [pendingNavigationRequest, tree.tree, zoom])

  const visibleTreeNodes = useUnsafeMemo(() => {
    return filterVisibleTreeNodes(tree.tree, visibleBounds, NODE_SIZE)
  }, [tree.tree, visibleBounds])

  const visibleTreeNodeSet = useUnsafeMemo(() => {
    return new Set(visibleTreeNodes)
  }, [visibleTreeNodes])

  const links = useUnsafeMemo(() => {
    const [nodeWidth, nodeHeight] = NODE_SIZE

    return tree.tree.flatMap((pointNode) => {
      if (!pointNode.parent || !pointNode.parent.parent) {
        return []
      }

      const isVisibleEndpoint =
        visibleTreeNodeSet.has(pointNode) || visibleTreeNodeSet.has(pointNode.parent)

      if (!isVisibleEndpoint) {
        return []
      }

      const parentLink = linkGenerator({
        source: [pointNode.parent.x + nodeWidth / 2, pointNode.parent.y + nodeHeight],
        target: [pointNode.x + nodeWidth / 2, pointNode.y],
      })
      const linkKey = `${getPointNodeKey(pointNode.parent)}-${getPointNodeKey(pointNode)}`

      return [<Node.Link key={`link-${linkKey}`} d={parentLink} />]
    })
  }, [tree.tree, visibleTreeNodeSet])

  const employeeNode = (pointNode: PointNode) => {
    if (pointNode.data.type !== 'employee') return null

    const employeeData = pointNode.data
    const employee = employeeData.employee

    if (!employee) return

    const [, nodeHeight] = NODE_SIZE
    const id = employee.id

    const status = getEmployeeNodeStatus({
      employeeId: id,
      filteredEmployeeIds,
      highlightedEmployeeId,
      search,
      searchResultId,
    }) satisfies NodeStatus | undefined

    const handleNodeClick = () => {
      update('activeEmployee', employee)
      track(trackingEvents.orgChart.nodes.employee.click)
    }

    const handleCollapseChange = () => {
      const nextUncollapsedNodes = uncollapsedNodes.toggle(id)
      const nextTree = tree.build(heads, employees, nextUncollapsedNodes)

      const nextPointNode = findTreeNodeById(nextTree, id)

      if (!nextPointNode) return

      if (nextUncollapsedNodes.has(id)) {
        zoom.navigateToCoordinates(nextPointNode.x, nextPointNode.y + nodeHeight)
        track(trackingEvents.orgChart.nodes.managers.click)
      } else {
        zoom.navigateToNode(nextPointNode)
      }
    }

    return (
      <Fragment key={`employee-${id}`}>
        <Node.Employee
          data={employeeData}
          tagInfoProp={tagInfoProp}
          x={pointNode.x}
          y={pointNode.y}
          status={status}
          onClick={handleNodeClick}
          onCollapseChange={handleCollapseChange}
          canSeeJobTitle={canSeeJobTitle}
          canSeeJobCatalog={canSeeJobCatalog}
          canSeeJobCatalogTree={canSeeJobCatalogTree}
          hasLegacyJobCatalog={hasLegacyJobCatalog}
        />
      </Fragment>
    )
  }

  const addPositionNode = (pointNode: PointNode) => {
    if (pointNode.data.type !== 'add-position') return null

    const addPositionData = pointNode.data

    if (!hintedNode && !hideOrgChartPositionsHints) {
      update('hintedNode', pointNode)
      zoom.navigateToNode(pointNode)
    }

    const node = (
      <Node.AddPosition
        key={`add-position-${pointNode.x}-${pointNode.y}`}
        x={pointNode.x}
        y={pointNode.y}
        data={addPositionData}
        refetch={tree.refetch}
      />
    )

    return hintedNode?.data.managerId === addPositionData.managerId ? (
      <Node.AddPosition.Hint
        key={`add-position-hint-${pointNode.x}-${pointNode.y}`}
        x={pointNode.x}
        y={pointNode.y}
      >
        {node}
      </Node.AddPosition.Hint>
    ) : (
      <Fragment key={`add-position-${pointNode.x}-${pointNode.y}`}>{node}</Fragment>
    )
  }

  const positionNode = (pointNode: PointNode) => {
    if (pointNode.data.type !== 'position') return null

    const positionData = pointNode.data

    return (
      <Fragment key={`position-${positionData.positionId}`}>
        <Node.Position data={positionData} x={pointNode.x} y={pointNode.y} refetch={tree.refetch} />
      </Fragment>
    )
  }

  /**
   * Virtualization: only render nodes whose bounding box intersects the
   * padded viewport reported by `useZoom`. With ~1.6k employees, the
   * browser paying layout + paint cost on every non-visible
   * `<foreignObject>` dominates pan performance (measured ~1.2-1.8s of
   * long tasks per pan). Culling drops the rendered set to ~dozens and
   * removes virtually all pan jank.
   */
  const treeNodes = visibleTreeNodes.map((x) => {
    switch (x.data.type) {
      case 'employee':
        return employeeNode(x)
      case 'position':
        return positionNode(x)
      case 'add-position':
        return addPositionNode(x)
      default:
        return null
    }
  })

  useUnsafeEffect(() => {
    if (svgRef.current) {
      // On first load zoom out a bit from the initial coordinates
      if (zoom.isReady) {
        zoom.scale(0.8)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [zoom.isReady])

  const exportOrgChart = () => {
    try {
      app.setProgress({
        message: i18n.t('employees.orgchart.export.download'),
      })

      const url = buildUrl('/export/orgchart')
      window.location.href = url
    } catch (exception) {
      // eslint-disable-next-line no-console
      console.error(exception)
      app.setFlash({
        type: 'wrong',
        message: i18n.t('errors.generic_requesting'),
      })
    } finally {
      app.setProgress(null)
    }
  }
  const downloadPdf = () => {
    const svgElement = document.getElementById('orgchart-div')
    try {
      app.setProgress({
        message: i18n.t('employees.orgchart.download.message'),
      })

      if (svgElement) {
        takeSnapshot(svgElement)
      }
    } catch (exception) {
      // eslint-disable-next-line no-console
      console.error(exception)
      Sentry.captureException(exception)

      app.setFlash({
        type: 'wrong',
        message: i18n.t('errors.generic_requesting'),
      })
    } finally {
      app.setProgress(null)
    }
  }

  const options = []
  const exportOption = {
    onClick: exportOrgChart,
    title: i18n.t('employees.orgchart.export.title'),
    description: i18n.t('employees.orgchart.export.description'),
  }

  const downloadOption = {
    onClick: downloadPdf,
    title: i18n.t('employees.orgchart.download.title'),
    description: i18n.t('employees.orgchart.download.description'),
  }

  if (canExport) {
    options.push(exportOption)
  }

  if (canSnapshot) {
    options.push(downloadOption)
  }

  return (
    <Hint.Popover.Provider
      steps={2}
      isHidden={!!hideOrgChartPositionsHints || !canManagePositions}
      onChange={handleHintChange}
      onClose={handleHintClose}
      onFinish={handleHintFinish}
    >
      <>
        <Header onFindMe={handleFindMe} options={options}>
          <Box>
            <TagInfoSelect
              initialValue={tagInfoProp}
              onTagInfoChange={(x) => update('tagInfoProp', x)}
              showLegalGenders={showLegalGenders}
            />
          </Box>
          <Box width="full">
            <SearchField
              variant="big"
              onSearch={onSearch}
              search={search}
              placeholder={capitalize(i18n.t('common.search'))}
            />
          </Box>
          <Box flexNoShrink>
            <SearchResultsNavigator
              results={filteredEmployees}
              search={search}
              onResultChange={handleResultChange}
            />
          </Box>
        </Header>
        <Box style={{ position: 'relative' }}>
          <Frame ref={svgRef} contentRef={contentRef} width={width} height={frameHeight}>
            {links}
            {treeNodes}
          </Frame>
          {!!height && (
            <ActionBar
              onClick={zoom.scale}
              isFullyUncollapsed={isFullyUncollapsed}
              onUncollapseAll={handleUncollapseAll}
            />
          )}
          <SidePanel
            onClose={() => update('activeEmployee', undefined)}
            allEmployees={allEmployees}
            employeeMap={employeeMap}
            canSeeJobTitle={canSeeJobTitle}
            canSeeJobCatalog={canSeeJobCatalog}
            canSeeJobCatalogTree={canSeeJobCatalogTree}
            hasLegacyJobCatalog={hasLegacyJobCatalog}
          />
        </Box>
      </>
    </Hint.Popover.Provider>
  )
})

export default OrgChartRef
